import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Grid, List, ListItem,
  ListItemText, ListItemIcon, Chip, Alert, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton
} from '@mui/material';
import {
  QrCode as ScanIcon, 
  TrendingUp as ToSiteIcon,
  TrendingDown as ToStackIcon,
  Refresh as RefreshIcon,
  Assignment as JobIcon
} from '@mui/icons-material';
import BarcodeScanner from './BarcodeScanner';
import FeatureGate from './FeatureGate';
import { jobAssignmentApi } from '../services/api.service';
import { useSnackbar } from 'notistack';

export default function QuickStackScanner() {
  const [showScanner, setShowScanner] = useState(false);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [scannedBarcode, setScannedBarcode] = useState('');
  const [recentActivities, setRecentActivities] = useState([]);
  const [jobStacks, setJobStacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleBarcodeScan = (barcode) => {
    setScannedBarcode(barcode);
    setShowScanner(false);
    setShowActionDialog(true);
  };

  const handleStackAction = async (action) => {
    try {
      setLoading(true);
      const response = await jobAssignmentApi.updateStackByBarcode({
        barcode: scannedBarcode,
        action,
        location: action === 'to_site' ? 'SITE' : 'WAREHOUSE'
      });
      
      const result = response.data || response;
      enqueueSnackbar(
        `Equipment ${action.replace('_', ' ')} successfully`, 
        { variant: 'success' }
      );
      
      setShowActionDialog(false);
      setScannedBarcode('');
      loadJobStacks();
      
    } catch (error) {
      console.error('Stack update error:', error);
      enqueueSnackbar(error.message || 'Failed to update equipment location', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadJobStacks = async () => {
    try {
      const response = await jobAssignmentApi.getAllJobStacks({ limit: 10 });
      const data = response.data || response;
      setJobStacks(data.stacks || []);
    } catch (error) {
      console.error('Error loading job stacks:', error);
      // Don't show error to user for background loading
    }
  };

  useEffect(() => {
    loadJobStacks();
  }, []);

  return (
    <FeatureGate feature="QUICK_STACK_SCANNING">
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Quick Stack Updates
          </Typography>
          <IconButton onClick={loadJobStacks} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
        
        <Alert severity="info" sx={{ mb: 3 }}>
          Scan any equipment QR code to quickly update its location.
          Equipment must be assigned to a job first.
        </Alert>

        <Box textAlign="center" mb={3}>
          <Button
            variant="contained"
            size="large"
            startIcon={<ScanIcon />}
            onClick={() => setShowScanner(true)}
            sx={{ minWidth: 250, py: 2 }}
          >
            Scan Equipment QR Code
          </Button>
        </Box>

        {showScanner && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Scan Equipment Barcode
              </Typography>
              <BarcodeScanner
                onScanSuccess={handleBarcodeScan}
                onClose={() => setShowScanner(false)}
              />
              <Box mt={2}>
                <Button 
                  onClick={() => setShowScanner(false)}
                  variant="outlined"
                >
                  Cancel Scan
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {jobStacks.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Active Job Stacks
            </Typography>
            <Grid container spacing={2}>
              {jobStacks.slice(0, 6).map((stack) => (
                <Grid item xs={12} sm={6} md={4} key={stack.job_id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <JobIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="subtitle1" fontWeight="medium">
                          Job {stack.job_number || stack.job_id}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        {stack.job_name || 'Equipment Installation'}
                      </Typography>
                      
                      <Typography variant="body2" gutterBottom>
                        Location: {stack.stack_location || 'WAREHOUSE'}
                      </Typography>
                      
                      <Grid container spacing={1} mt={1}>
                        <Grid item xs={4}>
                          <Box textAlign="center">
                            <Typography variant="h6" color="primary">
                              {stack.total_items || 0}
                            </Typography>
                            <Typography variant="caption">
                              Total
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={4}>
                          <Box textAlign="center">
                            <Typography variant="h6" color="warning.main">
                              {stack.items_on_stack || 0}
                            </Typography>
                            <Typography variant="caption">
                              In Stack
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={4}>
                          <Box textAlign="center">
                            <Typography variant="h6" color="success.main">
                              {stack.items_on_site || 0}
                            </Typography>
                            <Typography variant="caption">
                              On Site
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                      
                      {stack.completion_percentage !== undefined && (
                        <Box mt={1}>
                          <Chip 
                            label={`${Math.round(stack.completion_percentage)}% deployed`}
                            color={stack.completion_percentage > 75 ? 'success' : 'warning'}
                            size="small"
                          />
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Action Selection Dialog */}
        <Dialog 
          open={showActionDialog} 
          onClose={() => !loading && setShowActionDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Equipment Location Update
          </DialogTitle>
          <DialogContent>
            <Typography gutterBottom>
              Equipment: <strong>{scannedBarcode}</strong>
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Where is this equipment going?
            </Typography>
            
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={6}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                  onClick={() => handleStackAction('to_site')}
                >
                  <CardContent sx={{ textAlign: 'center' }}>
                    <ToSiteIcon color="warning" sx={{ fontSize: 48, mb: 1 }} />
                    <Typography variant="h6">
                      To Job Site
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Remove from warehouse stack
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={6}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                  onClick={() => handleStackAction('return_to_stack')}
                >
                  <CardContent sx={{ textAlign: 'center' }}>
                    <ToStackIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
                    <Typography variant="h6">
                      Return to Stack
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Back to warehouse stack
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setShowActionDialog(false)}
              disabled={loading}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </FeatureGate>
  );
}